import { Component, OnInit, Input} from '@angular/core';
import { AssessmentAiStatus, FootStrikePattern, ImpairmentCategory, RunningDirection, TwoDimensionalAiAssessmentViewModelRead, TwoDimensionalAssessmentResult } from 'src/app/_models/generatedModels';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentService } from 'src/app/_services/generatedServices';
import { Enums } from 'src/app/_models/generatedEnums';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from '../../_services/toaster.service';
import { PublicVideoPopupComponent } from './public-video-popup.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, interval, timer } from 'rxjs';
import { ScoreDetailsPopupComponent } from './score-details-popup.component';
import { GeneralDetailsPopupComponent } from './general-details-popup.component';
import { CategoryDetailsPopupComponent } from './category-details-popup.component';
import { ViewportScroller } from '@angular/common';

@Component({
  selector: 'public-two-dimensional-ai-result-details',
  templateUrl: 'public-two-dimensional-ai-result-details.component.html',
  styleUrls: ['public-two-dimensional-ai-result-details.component.scss']
})
export class PublicTwoDimensionalAiAssessmentResultDetailsComponent implements OnInit
{
  initialized = false;
  analysis: TwoDimensionalAiAssessmentViewModelRead;
  analysisId: any;
  shareableLink: string;
  categoryScoreEnum = Enums.CategoryScoreEnum;
  footStrikePatternEnum = Enums.FootStrikePatternEnum;
  footStrikePattern = FootStrikePattern;
  speedUnitEnum = Enums.SpeedUnitEnum;
  distanceUnitEnum = Enums.DistanceUnitEnum;
  runningDirection = Enums.RunningDirectionEnum;
  assessmentAiStatusEnum = Enums.AssessmentAiStatusEnum;
  assessmentAiStatus = AssessmentAiStatus;
  impairmentCategoryEnum = Enums.ImpairmentCategoryEnum;
  runningCueEnum = Enums.RunningCueEnum;

  timerSubscription?: Subscription;
  videoProposedChanges: { videoType: RunningDirection; proposedChanges: string[] }[] = [];

  @Input()
  isCoachView: boolean = false;

  constructor(private route: ActivatedRoute, private assessmentData: AssessmentService, private router: Router, private toastr: ToasterService, private modalService: NgbModal, private viewportScroller: ViewportScroller) { }

  ngOnInit(): void
  {
    this.refreshData();
  }

  refreshData()
  {
    this.analysisId = this.route.snapshot.params ? this.route.snapshot.params['id'] : null;

    const isNumber = !isNaN(Number(this.analysisId));
    const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(this.analysisId);

    if (isNumber && this.isCoachView) {
      this.assessmentData.getTwoDimensionalAiAssessment(this.analysisId).subscribe((result) =>
      {
        this.analysis = result;
        this.initialized = true;

        if (this.analysis.status !== AssessmentAiStatus.Failed && this.analysis.status !== AssessmentAiStatus.Completed) {
          this.timerSubscription = timer(10000).subscribe(() =>
          {
            this.refreshData();
          });
        } else {
          this.clearTimer();
        }
      },
        (err: HttpErrorResponse) =>
        {
          if (err && err.error && err.error.indexOf('UnauthorizedAccessException') >= 0) {
            this.toastr.error('You do not have access to the assessment', 'Error',);
          } else {
            this.toastr.error('An error occurred while loading the assessment', 'Error');
          }

          this.router.navigate(['../../../'], { relativeTo: this.route });
        });
    } else if (isGuid) {
      this.assessmentData.getTwoDimensionalAiAssessmentByPublicId(this.analysisId).subscribe((result) =>
      {
        this.analysis = result;

        this.initialized = true;

        if (this.analysis.status !== AssessmentAiStatus.Failed && this.analysis.status !== AssessmentAiStatus.Completed) {
          this.timerSubscription = timer(10000).subscribe(() =>
          {
            this.refreshData();
          });
        } else {
          this.clearTimer();
        }
      },
        (err: HttpErrorResponse) =>
        {
          if (err && err.error && err.error.indexOf('UnauthorizedAccessException') >= 0) {
            this.toastr.error('You do not have access to the assessment', 'Error',);
          } else {
            this.toastr.error('An error occurred while loading the assessment', 'Error');
          }

          this.router.navigate(['../../../'], { relativeTo: this.route });
        });
    }
  }

  removeNegtiveSign(value: number): number
  {
    return Math.abs(value);
  }

  getLabelPelvicDrop(value: number, isLeft: boolean)
  {
    if (value < 0) {
      return isLeft ? 'lft up' : 'rgt up'
    }
    else {
      return isLeft ? 'lft down' : 'rgt down'
    }
  }

  getLabelForwardLean(value: number)
  {
    if (value < 0) {
      return 'ext'
    }
    else {
      return 'flex';
    }
  }

  getLabelHipAdduction(value: number)
  {
    if (value < 0) {
      return 'add'
    }
    else {
      return 'abd';
    }
  }

  getLabelTibialInclination(value: number)
  {
    if (value < 0) {
      return 'ext'
    }
    else {
      return 'flex';
    }
  }

  getLabelAnklePronatoin(value: number)
  {
    if (value < 0) {
      return 'sup'
    }
    else {
      return 'pron';
    }
  }

  getLabeCrossover(value: number)
  {
    if (value < 0) {
      return 'add'
    }
    else {
      return 'abd';
    }
  }

  getLabelFootInclination(value: number)
  {
    if (value < 0) {
      return 'DF'
    }
    else {
      return 'PL';
    }
  }

  getGeneralScoreLevel()
  {
    if (this.analysis.score >= 90)
      return "Excellent";
    else if (this.analysis.score >= 80 && this.analysis.score < 90)
      return "Good";
    else if (this.analysis.score >= 60 && this.analysis.score < 80)
      return "Fair";
    else
      return "Poor";
  }

  getGeneralScoreClass()
  {
    if (this.analysis.score >= 90)
      return "g";
    else if (this.analysis.score >= 80 && this.analysis.score < 90)
      return "gf";
    else if (this.analysis.score >= 60 && this.analysis.score < 80)
      return "f";
    else
      return "p";
  }

  getGeneralScoreDescription()
  {
    if (this.analysis.score >= 90)
      return "Your score is Excellent! Your running form is highly efficient and puts you at low risk of injury.";
    else if (this.analysis.score >= 80 && this.analysis.score < 90)
      return "Your score is Good. Your form is generally healthy but could benefit from small adjustments.";
    else if (this.analysis.score >= 60 && this.analysis.score < 80)
      return "Your score is Fair. Your form shows noticeable inefficiencies that may increase your injury risk.";
    else
      return "Your score is Poor. Your form has significant issues that put you at high risk of injury.";
  }

  getCategoryDescription()
  {
    if (this.analysis.impairmentCategory == ImpairmentCategory.OverstriderGeneral)
      return "Overstriding happens when your foot lands too far in front of your body, which can slow you down and raise your risk of injury.";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GeneralCadence)
      return "Low Cadence means your step rate is too low, which can increase strain on your body and limit your speed.";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Bouncer)
      return "Bouncing means there is too much up-and-down motion, which wastes energy and increases stress on your body.";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Collapser)
      return "Collapsing is when the ankle, knee, or hip rotate inward, adding stress and raising injury risk.";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GluteAmnesiac)
      return "Glute Amnesiac happens when a lack of forward lean limits glute use and shifts extra stress to the front of the body.";
    else
      return "Performance Optimization means your form is strong overall and can be refined to further improve efficiency and performance";
  }

  getCategoryInSummary()
  {
    if (this.analysis.impairmentCategory == ImpairmentCategory.OverstriderGeneral)
      return "an Overstrider";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GeneralCadence)
      return "a Low Cadence runner";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Bouncer)
      return "a Bouncer";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Collapser)
      return "a Collapser";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GluteAmnesiac)
      return "a Glute Amnesiac";
    else
      return "a Performance Optimizer";
  }

  getHookDescription()
  {
    if (this.analysis.score >= 90)
      return "Nice job, with a score of " + Math.round(this.analysis.score) + "% your running form is highly efficient. Some fine-tuning can ensure you hit your peak performance!";
    else if (this.analysis.score >= 80 && this.analysis.score < 90)
      return "Great work, your score of " + Math.round(this.analysis.score) + "% demonstrates a strong foundation for improvement to take you to the next level as a runner!";
    else if (this.analysis.score >= 60 && this.analysis.score < 80)
      return "Nice, your score of " + Math.round(this.analysis.score) + "% shows that some key adjustments would lead to big improvements in performance and reduced injury risk!";
    else
      return "I have good news! Your score of " + Math.round(this.analysis.score) + "% shows strong opportunities for improvement and the right plan will drive huge gains!";
  }

  getCue()
  {
    if (this.analysis.impairmentCategory == ImpairmentCategory.OverstriderGeneral)
      return "landing closer to your body";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GeneralCadence)
      return "increasing your step rate";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Bouncer)
      return "reducing vertical movement";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Collapser)
      return "maintaining a strong hip/pelvis position";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GluteAmnesiac)
      return "increasing your forward lean";
    else
      return "fine-tuning your mechanics";
  }

  getCategoryImage()
  {
    if (this.analysis.impairmentCategory == ImpairmentCategory.OverstriderGeneral)
      return "overstrider-icon.png";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GeneralCadence)
      return "low-cadence-icon.png";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Bouncer)
      return "bouncer-icon.png";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.Collapser)
      return "collapser-icon.png";
    else if (this.analysis.impairmentCategory == ImpairmentCategory.GluteAmnesiac)
      return "glute-amnesiac-icon.png";
    else
      return "performance-optimization-icon.png";
  }

  showBackVideo()
  {
    const modalRef = this.modalService.open(PublicVideoPopupComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    let videoBack = this.analysis.videos.find(v => v.videoType === RunningDirection.Back);

    modalRef.componentInstance.analysisName = this.runningDirection.get(videoBack.videoType) + ": " + videoBack.video.fileNameDisplay;

    if (videoBack.overlappedVideo) {
      modalRef.componentInstance.videoLink = videoBack.overlappedVideo.assetPath;
      modalRef.componentInstance.encoded = videoBack.overlappedVideo.encoded;
    } else {
      modalRef.componentInstance.videoLink = videoBack.video.assetPath;
      modalRef.componentInstance.encoded = videoBack.video.encoded;
      this.toastr.info('Your AI-adjusted video file is being prepared. Check back in a few minutes to see the result.', 'Info',);
    }

    modalRef.componentInstance.description = videoBack.description;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  showSideVideo()
  {
    const modalRef = this.modalService.open(PublicVideoPopupComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    let videoSide = this.analysis.videos.find(v => v.videoType !== RunningDirection.Back);

    modalRef.componentInstance.analysisName = this.runningDirection.get(videoSide.videoType) + ": " + videoSide.video.fileNameDisplay;

    if (videoSide.overlappedVideo) {
      modalRef.componentInstance.videoLink = videoSide.overlappedVideo.assetPath;
      modalRef.componentInstance.encoded = videoSide.overlappedVideo.encoded;
    } else {
      modalRef.componentInstance.videoLink = videoSide.video.assetPath;
      modalRef.componentInstance.encoded = videoSide.video.encoded;
      this.toastr.info('Your AI-adjusted video file is being prepared. Check back in a few minutes to see the result.', 'Info',);
    }
    modalRef.componentInstance.description = videoSide.description;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  showCategoryDescription()
  {
    const modalRef = this.modalService.open(CategoryDetailsPopupComponent, { windowClass: 'custom-modal-width', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.category = this.analysis.impairmentCategory;
    modalRef.componentInstance.analysis = this.analysis;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  showScoreDescription()
  {
    const modalRef = this.modalService.open(ScoreDetailsPopupComponent, { windowClass: 'custom-modal-width', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.score = this.analysis.score;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  showFormDescription(section: string)
  {
    const modalRef = this.modalService.open(GeneralDetailsPopupComponent, { windowClass: 'custom-modal-width', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.analysis = this.analysis;
    modalRef.componentInstance.section = section;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  overrideScore(scoreLevel: string)
  {
    if (scoreLevel == 'G')
      return "E";
    else if (scoreLevel == 'GF')
      return "G";

    return scoreLevel;
  }


  getVideoProposedChanges(): void
  {
    this.videoProposedChanges = [];

    if (this.analysis?.videos?.length) {
      this.analysis.videos.forEach((video: any) =>
      {
        const videoType = video.videoType;
        const rawChanges = video.qualityValidationProposedChanges || '';
        const proposedChanges = rawChanges
          .split(';')                  // split by semicolon
          .map(s => s.trim())          // remove extra spaces
          .filter(s => s.length > 0);  // ignore empty parts

        this.videoProposedChanges.push({
          videoType,
          proposedChanges
        });
      });
    }
  }

  goToFreePlan()
  {
    window.open('https://cl74f.share.hsforms.com/283TV-Ix4T_Sg37E1BUrDNQ', '_blank');
  }

  clearTimer()
  {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  scrollToSection(event: Event, sectionId: string): void
  {
    event.preventDefault();
    this.viewportScroller.scrollToAnchor(sectionId);
  }

  ngOnDestroy()
  {
    // Clean up timer subscription
    this.clearTimer();
  }
}
